// WelcomeScreen.tsx — Landing screen shown when no project is loaded
// Buttons: New Project | Open Project | Recent Projects list

import { useStore } from '../store/useStore';
import { useEffect, useState } from 'react';
import { db } from '../store/db';
import { useAuth } from '../auth/AuthProvider';
import cvLogoUrl from '/cv-logo.jpg?url';

interface RecentEntry {
  id: string;
  name: string;
  updatedAt: string;
  snapshots: number;
  lastView?: string;
}

const SAMPLES: { key: 'arlington' | 'basilica'; label: string; desc: string }[] = [
  { key: 'arlington', label: 'Arlington Airport',  desc: '83-panel terminal — 2 floors, stepped footprint, brace + cast layout' },
  { key: 'basilica',  label: 'St. Anthony Basilica', desc: '16-panel nave — single floor with brace + cast layout' },
];

interface WelcomeScreenProps {
  onSignInRequested?: () => void;
}

export default function WelcomeScreen({ onSignInRequested }: WelcomeScreenProps = {}) {
  const setActiveDialog   = useStore(s => s.setActiveDialog);
  const loadSampleProject = useStore(s => s.loadSampleProject);
  const { session, user, canEdit, balance, signOut } = useAuth();

  const [recent, setRecent]           = useState<RecentEntry[]>([]);
  const [sampleOpen, setSampleOpen]   = useState(false);

  useEffect(() => {
    (async () => {
      const rows = await db.projects
        .orderBy('updatedAt')
        .reverse()
        .limit(8)
        .toArray();
      const entries: RecentEntry[] = [];
      for (const r of rows) {
        const snapshots = await db.snapshots
          .where('projectId').equals(r.id)
          .count();
        entries.push({ id: r.id, name: r.name, updatedAt: r.updatedAt, snapshots, lastView: r.lastView });
      }
      setRecent(entries);
    })();
  }, []);

  function openRecent(id: string) {
    useStore.getState().loadProject(id);
  }

  function openRecentWithSnapshots(id: string) {
    useStore.getState().loadProject(id);
    // Small delay so project loads before opening snapshot browser
    setTimeout(() => useStore.getState().setActiveDialog('snapshots'), 100);
  }

  function viewLabel(lv?: string): string {
    if (!lv) return '';
    switch (lv) {
      case 'project':  return 'Project';
      case 'site-2d':  return 'Site 2D';
      case 'site-3d':  return 'Site 3D';
      case 'panel-2d': return 'Panel 2D';
      case 'panel-3d': return 'Panel 3D';
      default: return '';
    }
  }

  return (
    <div className="welcome-screen">
      <a href="../index.html" className="welcome-back">&#8592; SimpleStruct</a>
      <div className="welcome-card">
        <img src={cvLogoUrl} alt="ConstructiVision" className="welcome-logo-img" />
        <h1 className="welcome-title">ConstructiVision Web</h1>
        <p className="welcome-subtitle">Tilt-Up Concrete Panel Detailing</p>

        <div className="welcome-actions">
          <button
            className="welcome-btn primary"
            onClick={() => useStore.getState().newProject()}
          >
            New Project
          </button>
          <button
            className="welcome-btn"
            onClick={() => setActiveDialog('project-load')}
          >
            Open Project
          </button>
          <button
            className="welcome-btn"
            onClick={() => setActiveDialog('import-cv')}
          >
            Import CV File…
          </button>
          <div className="welcome-sample-picker">
            <button
              className="welcome-btn"
              onClick={() => setSampleOpen(o => !o)}
            >
              Load Sample Project ▾
            </button>
            {sampleOpen && (
              <div className="welcome-sample-menu">
                {SAMPLES.map(s => (
                  <button
                    key={s.key}
                    className="welcome-sample-item"
                    onClick={() => { setSampleOpen(false); loadSampleProject(s.key); }}
                  >
                    <span className="welcome-sample-label">{s.label}</span>
                    <span className="welcome-sample-desc">{s.desc}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {recent.length > 0 && (
          <div className="welcome-recent">
            <div className="welcome-recent-label">Recent</div>
            <ul>
              {recent.map(r => (
                <li key={r.id}>
                  <button
                    className="welcome-recent-item"
                    onClick={() => openRecent(r.id)}
                  >
                    <span className="welcome-recent-name">{r.name}</span>
                    <span className="welcome-recent-meta">
                      {viewLabel(r.lastView) && (
                        <span className="welcome-recent-view">{viewLabel(r.lastView)}</span>
                      )}
                      {r.snapshots > 0 && (
                        <span
                          className="welcome-recent-snaps"
                          title={`${r.snapshots} saved copies — click to browse`}
                          onClick={e => { e.stopPropagation(); openRecentWithSnapshots(r.id); }}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {r.snapshots} {r.snapshots === 1 ? 'save' : 'saves'}
                        </span>
                      )}
                      <span className="welcome-recent-date">
                        {new Date(r.updatedAt).toLocaleDateString()}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="welcome-account">
          {session ? (
            <>
              <span className="welcome-account-email">{user?.email ?? 'Signed in'}</span>
              {canEdit
                ? <span className="welcome-account-status">Member</span>
                : <span className="welcome-account-status warn">Read-only</span>}
              <span className="welcome-account-balance">{balance} tokens</span>
              <a className="welcome-account-link" href={`${import.meta.env.BASE_URL}pricing`}>Manage</a>
              <button className="welcome-account-link" onClick={() => signOut()}>Sign out</button>
            </>
          ) : (
            <>
              <button className="welcome-account-link" onClick={onSignInRequested}>Sign in</button>
              <a className="welcome-account-link" href={`${import.meta.env.BASE_URL}pricing`}>See pricing</a>
            </>
          )}
        </div>

        <div className="welcome-footer">
          CV-Web — Sprint 21
        </div>
      </div>
    </div>
  );
}
